/**
 * ExamStartConfirmation.tsx
 * Modern confirmation dialog before starting exam with important reminders
 */

import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@vsat/shared/components';
import { Button } from '@vsat/shared/components';
import { Badge } from '@vsat/shared/components';
import {
  AlertCircle,
  Clock,
  Shield,
  Wifi,
  Volume2,
  CheckCircle2,
  Play,
  BookOpen,
  FileText,
  Zap,
  Info,
  AlertTriangle,
} from 'lucide-react';

interface ExamStartConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  examTitle: string;
  examTitleVi: string;
  duration: number;
  questions: number;
  isPaid: boolean;
  onConfirm: () => void;
}

export function ExamStartConfirmation({
  open,
  onOpenChange,
  examTitle,
  examTitleVi,
  duration,
  questions,
  isPaid,
  onConfirm,
}: ExamStartConfirmationProps) {
  const { i18n } = useTranslation();
  const isVietnamese = true; // Force Vietnamese as requested

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const requirements = [
    {
      icon: Wifi,
      title: isVietnamese ? 'Kết nối internet ổn định' : 'Stable internet connection',
      description: isVietnamese
        ? 'Đảm bảo kết nối internet không bị gián đoạn trong suốt bài thi'
        : 'Ensure uninterrupted internet throughout the exam',
      color: 'text-info',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Volume2,
      title: isVietnamese ? 'Môi trường yên tĩnh' : 'Quiet environment',
      description: isVietnamese
        ? 'Chọn không gian yên tĩnh để tập trung làm bài tốt nhất'
        : 'Choose a quiet space for best concentration',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      icon: Shield,
      title: isVietnamese ? 'Không chuyển tab' : 'No tab switching',
      description: isVietnamese
        ? 'Không chuyển sang tab/cửa sổ khác (vi phạm 3 lần sẽ tự động nộp bài)'
        : 'Do not switch tabs/windows (3 violations = auto-submit)',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      icon: Clock,
      title: isVietnamese ? 'Quản lý thời gian' : 'Time management',
      description: isVietnamese
        ? 'Bài thi sẽ tự động nộp khi hết thời gian'
        : 'Exam will auto-submit when time expires',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  const tips = [
    {
      icon: CheckCircle2,
      text: isVietnamese
        ? 'Đọc kỹ đề trước khi chọn đáp án'
        : 'Read questions carefully before answering',
    },
    {
      icon: FileText,
      text: isVietnamese
        ? 'Sử dụng chức năng đánh dấu câu hỏi cần xem lại'
        : 'Use flag feature to mark questions for review',
    },
    {
      icon: Zap,
      text: isVietnamese
        ? 'Làm câu dễ trước, khó sau để tối ưu thời gian'
        : 'Answer easy questions first to optimize time',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] flex flex-col p-0 min-h-0'>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className='flex flex-col min-h-0'
            >
              {/* Header */}
              <div className='relative overflow-hidden bg-gradient-to-r from-primary via-primary/90 to-primary/80 p-6'>
                <div className='absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32' />
                <div className='absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24' />

                <div className='relative'>
                  <DialogHeader>
                    <div className='flex items-start gap-4'>
                      <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm'>
                        <Play className='h-7 w-7 text-white' />
                      </div>
                      <div className='flex-1'>
                        <DialogTitle className='text-xl font-bold text-white mb-1'>
                          {isVietnamese ? 'Sẵn sàng bắt đầu?' : 'Ready to Start?'}
                        </DialogTitle>
                        <DialogDescription className='text-primary-foreground/90 text-xs'>
                          {isVietnamese ? examTitleVi : examTitle}
                        </DialogDescription>
                      </div>
                      {isPaid && (
                        <Badge className='bg-gradient-to-r from-primary to-primary/80 text-white border-0 shadow-lg shadow-primary/25'>
                          <Shield className='h-3 w-3 mr-1' />
                          {isVietnamese ? 'Đã thanh toán' : 'Paid'}
                        </Badge>
                      )}
                    </div>
                  </DialogHeader>

                  <div className='mt-4 flex flex-wrap gap-3'>
                    <div className='flex items-center gap-2 rounded-lg bg-white/20 backdrop-blur-sm px-4 py-2'>
                      <Clock className='h-5 w-5 text-white' />
                      <span className='text-white font-semibold'>
                        {duration} {isVietnamese ? 'phút' : 'minutes'}
                      </span>
                    </div>
                    <div className='flex items-center gap-2 rounded-lg bg-white/20 backdrop-blur-sm px-4 py-2'>
                      <BookOpen className='h-5 w-5 text-white' />
                      <span className='text-white font-semibold'>
                        {questions} {isVietnamese ? 'câu hỏi' : 'questions'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className='flex-1 overflow-y-auto p-6 pb-28 min-h-0'>
                {/* Important Requirements */}
                <div className='mb-6'>
                  <div className='flex items-center gap-2 mb-3'>
                    <AlertCircle className='h-4 w-4 text-primary' />
                    <h3 className='text-base font-bold text-foreground'>
                      {isVietnamese ? 'Yêu cầu quan trọng' : 'Important Requirements'}
                    </h3>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {requirements.map((req, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className='rounded-xl border border-border bg-card/80 backdrop-blur-sm p-3 hover:shadow-lg hover:ring-2 hover:ring-primary/20 transition-all duration-300'
                      >
                        <div className='flex items-start gap-3'>
                          <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/80 text-primary-foreground'>
                            <req.icon className='h-5 w-5' />
                          </div>
                          <div className='flex-1'>
                            <h4 className='font-semibold text-foreground mb-1'>{req.title}</h4>
                            <p className='text-sm text-muted-foreground'>{req.description}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Tips */}
                <div className='mb-6'>
                  <div className='flex items-center gap-2 mb-3'>
                    <Info className='h-4 w-4 text-primary' />
                    <h3 className='text-base font-bold text-foreground'>
                      {isVietnamese ? 'Mẹo làm bài' : 'Exam Tips'}
                    </h3>
                  </div>

                  <div className='space-y-3'>
                    {tips.map((tip, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                        className='flex items-start gap-3 rounded-lg bg-primary/10 border border-primary/50 p-3 hover:bg-primary/20 transition-all duration-300'
                      >
                        <tip.icon className='h-5 w-5 text-primary shrink-0 mt-0.5' />
                        <p className='text-sm text-foreground'>{tip.text}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sticky Action Buttons */}
              <div className='absolute bottom-0 left-0 right-0 bg-card border-t border-border p-4'>
                <div className='flex flex-col sm:flex-row gap-3'>
                  <Button
                    variant='outline'
                    onClick={() => onOpenChange(false)}
                    className='flex-1 border border-border hover:bg-muted hover:border-primary/30 transition-all duration-300'
                  >
                    {isVietnamese ? 'Hủy' : 'Cancel'}
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    className='flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25 transition-all duration-300'
                  >
                    <Play className='h-5 w-5 mr-2' />
                    {isVietnamese ? 'Bắt đầu làm bài' : 'Start Exam'}
                  </Button>
                </div>
                <p className='text-xs text-center text-muted-foreground mt-2 leading-tight'>
                  {isVietnamese
                    ? 'Bằng cách nhấn "Bắt đầu làm bài", bạn đã đồng ý tuân thủ các quy định thi cử'
                    : 'By clicking "Start Exam", you agree to follow exam regulations'}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
